// src/components/workAccountability/CompletionHistory.tsx
//
// Part 8: immutable completion attempts. Each attempt shows who/when, attempt #,
// the snapshot (actual vs baseline), proof status, and applied policies. No
// editing. Fails closed (renders nothing) if the feature is off (503).

import { useGetTaskCompletionQuery } from '../../api/accountabilityApi';
import { formatMinutes, variancePct } from './waFormat';

interface Props {
  taskId: string;
  nameForUser?: (id: string) => string;
}

export default function CompletionHistory({ taskId, nameForUser }: Props) {
  const { data, isLoading, isError, error } = useGetTaskCompletionQuery(taskId, { skip: !taskId });

  // Feature disabled (503) or forbidden — render nothing (fail closed).
  if (isError && (error as { status?: number })?.status === 503) return null;

  return (
    <div data-testid="wa-completion-history">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500 mb-2">Completion History</div>
      {isLoading ? (
        <div className="h-16 w-full animate-pulse rounded-lg bg-gray-100" />
      ) : isError ? (
        <p className="text-sm text-gray-500">History unavailable.</p>
      ) : !data || data.records.length === 0 ? (
        <p className="text-sm text-gray-500">No completions yet.</p>
      ) : (
        <ul className="space-y-2">
          {data.records.map((rec) => {
            const v = variancePct(rec.actualMinutesSnapshot, rec.baselineEstimatedMinutes);
            return (
              <li key={rec.id} className="rounded-lg border border-gray-200 p-3 text-sm" data-testid={`wa-completion-${rec.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-gray-900">Attempt #{rec.attemptNo}</div>
                  <div className="text-xs text-gray-500">{rec.createdAt ? new Date(rec.createdAt).toLocaleString() : ''}</div>
                </div>
                <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-gray-600">
                  <div><span className="font-medium">Completed by:</span> {nameForUser?.(rec.completedByUserId) ?? rec.completedByUserId}</div>
                  <div><span className="font-medium">Proof:</span> {rec.proofSatisfied ? 'Satisfied' : '—'}</div>
                  <div><span className="font-medium">Actual:</span> {formatMinutes(rec.actualMinutesSnapshot)}</div>
                  <div><span className="font-medium">Baseline:</span> {formatMinutes(rec.baselineEstimatedMinutes)}</div>
                  {v != null && (
                    <div className="col-span-2"><span className="font-medium">Variance:</span> {v > 0 ? '+' : ''}{v}%</div>
                  )}
                  {rec.completionNote && <div className="col-span-2"><span className="font-medium">Note:</span> {rec.completionNote}</div>}
                  <div className="col-span-2"><span className="font-medium">Applied policies:</span> {rec.appliedPolicyIds.length || 'none'}</div>
                </dl>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
